/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* global grecaptcha, dataLayer */

import { Controller } from '@hotwired/stimulus';
import { dispatchEvent } from '@shared/script/eventDispatcher';
import { NotificationType } from '@shared/script/enums/notification';

type NewsletterResponse = {
    exists: boolean;
    message?: string;
}

/**
 * Had to fix a bit of a gotcha here with reCaptcha v2 and v3 and Stimulus.
 * Our global recaptcha script loader works nicely for legacy scripts/forms
 * However, we need to handle it the "Stimulus way" for this form. We can support both more gracefully though:
 *
 * 1. Rework the global handlers to export pure functions that can be called to load the scripts.
 * 2. Use them here to avoid the duplication of script loading logic.
 * 3. Integrate the loaders to the legacy handlers until we migrate all forms to Stimulus.
 *
 * At the moment the handlers and loaders are quite coupled in recaptcha.js so this is a stopgap for the patch.
 */
export default class extends Controller {
    static targets = ['submitButton'];

    declare submitButtonTarget: HTMLButtonElement;

    private v2CaptchaResolver: ((token: string) => void) | null = null;
    private v2CaptchaPromise: Promise<string> | null = null;

    connect(): void {
        const recaptcha_branding: string = this.submitButtonTarget?.dataset.recaptchaBranding ?? '';
        if (recaptcha_branding) {
            this.createBranding(recaptcha_branding);
        }
    }

    async subscribe(recaptchaKey: string = ''): Promise<void> {
        let success = true;
        const payload = new FormData(this.element);

        if (recaptchaKey) {
            // key is not in the form
            payload.append('g-recaptcha-response', recaptchaKey);
        }

        try {
            const response = await fetch(this.element.getAttribute('action'), {
                method: 'POST',
                body: payload
            });

            if (!response.ok) {
                dispatchEvent('notifications:show', NotificationType.Error);
                success = false;
            }

            const data: NewsletterResponse = await response.json();

            if (data?.message) {
                // something wrong, server tells us what's up
                dispatchEvent('notifications:hideAll');
                dispatchEvent('notifications:show', NotificationType.ErrorPlaceholder);

                this.element.querySelector('.notification--error.notification--placeholder .message').innerText = data.message;
                return;
            }

            if (!success) {
                return;
            }

            this.element.querySelectorAll('.form-group, .btn').forEach((el) => {
                el.classList.add('hidden');
            });

            if (data.exists) {
                // you're already subbed
                dispatchEvent('notifications:show', NotificationType.Notice);
                return;
            }

            this.element.classList.add('success');
            dispatchEvent('notifications:show', NotificationType.Success);
        } catch {
            dispatchEvent('notifications:show', NotificationType.Error);
        }
    }

    /**
     * Handles the form submission, including reCAPTCHA verification if required.
     * @param e - The event object from the form submission.
     */
    async handleSubmit(e: Event): Promise<void> {
        e.preventDefault();
        e.stopPropagation();

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        dataLayer.push({
            'event': 'form_submit',
            'eventCategory': 'newsletter_subscribe'
        });

        const recaptchaKey: string = this.submitButtonTarget?.dataset.recaptchaKey ?? '';
        const recaptchaVersion: string = this.submitButtonTarget?.dataset.recaptchaVersion ?? '3';

        if (recaptchaKey) {
            try {
                await this.ensureRecaptchaLoaded(recaptchaKey, recaptchaVersion);
            } catch {
                dispatchEvent('notifications:show', NotificationType.Error);
                return;
            }

            if (recaptchaVersion === '3') {

                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                grecaptcha.ready(async() => {
                    try {
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                        const token: string = await grecaptcha.execute(recaptchaKey, { action: 'submit' }) ?? '';
                        if (token) {
                            await this.subscribe(token);
                        }
                    } catch {
                        dispatchEvent('notifications:show', NotificationType.Error);
                    }
                });
            } else if (recaptchaVersion === '2') {
                if (!this.v2CaptchaPromise) {
                    throw new Error('reCAPTCHA v2 was not initialized');
                }

                const token = await this.v2CaptchaPromise;
                await this.subscribe(token);
            }
        } else {
            await this.subscribe();
        }
    }

    private createBranding(branding: string) : void {
        const brandingEl = document.createElement('div');
        brandingEl.classList.add('grecaptcha-branding');
        brandingEl.innerHTML = branding;
        this.element.parentNode.appendChild(brandingEl);
    }

    /**
     * Ensure the v3 reCAPTCHA script is loaded before trying to use it.
     * This is a brige as we are still using imperative script loading for recaptcha
     * elsewhere on the site, so it may already be loading or loaded.
     */
    private ensureRecaptchaLoaded(siteKey: string, recaptchaVersion: string): Promise<void> {
        if (recaptchaVersion === '3') {
            return this.loadRecaptchaV3(siteKey);
        }
        if (recaptchaVersion === '2') {
            return this.loadRecaptchaV2(siteKey);
        }

        return Promise.resolve();
    }

    private loadRecaptchaV3(siteKey: string): Promise<void> {
        return new Promise((resolve, reject) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (typeof grecaptcha !== 'undefined' && typeof grecaptcha.execute === 'function') {
                resolve();
                return;
            }

            const existingScript = document.querySelector('script[src*="recaptcha/api.js"]');

            if (existingScript) {
                // Already loading
                if (existingScript.getAttribute('data-loaded') === 'true') {
                    resolve();
                } else {
                    existingScript.addEventListener('load', () => resolve());
                    existingScript.addEventListener('error', () => reject());
                }
                return;
            }

            const script = document.createElement('script');
            script.src = `https://www.google.com/recaptcha/api.js?render=${siteKey}`;
            script.async = true;
            script.defer = true;
            script.addEventListener('load', () => {
                script.setAttribute('data-loaded', 'true');
                resolve();
            });
            script.addEventListener('error', () => reject());

            document.head.appendChild(script);
        });
    }

    private loadRecaptchaV2(siteKey: string): Promise<void> {
        return new Promise((resolve, reject) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (typeof grecaptcha !== 'undefined' && typeof grecaptcha.render === 'function') {
                this.renderRecaptchaV2(siteKey);
                resolve();
                return;
            }

            const container = document.createElement('div');
            container.classList.add('g-recaptcha');
            this.submitButtonTarget.parentNode?.insertBefore(container, this.submitButtonTarget);

            (window as unknown).recaptchaCallback = (): void => {
                try {
                    this.renderRecaptchaV2(siteKey, container);
                    resolve();
                } catch (err) {
                    reject(err);
                }
            };

            const existingScript = document.querySelector<HTMLScriptElement>('script[src*="recaptcha/api.js"]');
            if (existingScript) {
                if (existingScript.getAttribute('data-loaded') === 'true') {
                    (window as unknown).recaptchaCallback();
                } else {
                    existingScript.addEventListener('load', () => (window as unknown).recaptchaCallback());
                    existingScript.addEventListener('error', () => reject());
                }
                return;
            }

            const script = document.createElement('script');
            script.async = true;
            script.defer = true;
            script.src = 'https://www.google.com/recaptcha/api.js'
              + '?hl=' + document.documentElement.lang
              + '&onload=recaptchaCallback&render=explicit';

            script.addEventListener('load', () => {
                script.setAttribute('data-loaded', 'true');
            });
            script.addEventListener('error', () => reject());

            document.head.appendChild(script);
        });
    }

    private renderRecaptchaV2(siteKey: string, container?: HTMLElement): void {
        const target = container ?? document.querySelector('.g-recaptcha');
        if (!target) {
            throw new Error('reCAPTCHA container not found');
        }

        this.v2CaptchaPromise = new Promise((resolve) => {
            this.v2CaptchaResolver = resolve;
        });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        grecaptcha.render(target, {
            sitekey: siteKey,
            theme: 'light',
            callback: (token: string) => {
                this.submitButtonTarget.disabled = false;
                if (this.v2CaptchaResolver) {
                    this.v2CaptchaResolver(token);
                    this.v2CaptchaResolver = null;
                }
            },
        });
    }
}
